<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import {computed} from "vue";
import {useProductStore} from "@js/store/ProductStore.js";
import {ecomClick, mapProduct} from "@js/ecommerce.js";
import CartQuantityStepper from "@js/components/Sections/Cart/Components/CartQuantityStepper.vue";
import ProductPriceConfigurator from "@js/components/Sections/Catalog/Components/ProductPriceConfigurator.vue";

export default {
    name: "CatalogProductItemGrid",
    components: {
        ProductPriceConfigurator,
        CartQuantityStepper,
        IconCheckMark,
        IconScales,
        NumberFormatComponent,
        IconCartPlus,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link,
    },
    props: {
        data: {type: Object, required: true},
        listName: {type: String, default: 'Каталог'},
        position: {type: Number, default: 1},
    },
    methods: {
        trackClick() {
            try {
                ecomClick(mapProduct(this.data, {list: this.listName, position: this.position}));
            } catch (e) {}
        },
        imageSrc() {
            const image = this.data?.images?.[0];
            return image ? '/storage/products/' + image : '/images/no-image.webp';
        },
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cartStore = useCartStore();
        const productStore = useProductStore();

        cartStore.registerCatalogProduct(props.data);

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.data.id).qty,
            set: (val) => cartStore.setQtyFromInput(props.data.id, val),
        });

        const canBuy = computed(() => props.data.available && cartStore.priceActive(props.data.id) > 0);

        const categoryLabel = computed(() => {
            return props.data?.sub_category?.title || props.data?.category?.title || '';
        });

        return {
            likeStore,
            compareStore,
            cartStore,
            productStore,
            qtyProxy,
            canBuy,
            categoryLabel,
        };
    },
};
</script>

<template>
    <article class="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:border-gray-300 hover:shadow-sm">
        <div class="flex h-full flex-col p-3">
            <div class="grid grid-cols-[100px_1fr] gap-3 items-start">
                <div class="relative">
                    <Link :href="route('product', data.slug)" @pointerdown="trackClick">
                        <ImageLoader
                            class="block w-[100px] h-[100px] rounded-lg border border-gray-200 overflow-hidden bg-white"
                            :title="data.title"
                            :src="imageSrc()"
                            imgClasses="w-full h-full object-contain"
                        />
                    </Link>
                </div>

                <div class="min-w-0">
                    <div class="flex items-start justify-between gap-2">
                        <div
                            class="w-min text-nowrap text-[10px] px-1.5 py-[2px] rounded"
                            :class="[data.available ? 'bg-emerald-600 text-white': 'bg-amber-400 text-black']"
                        >
                            {{ data.available ? 'В наличии' : 'Под заказ' }}
                        </div>

                        <div class="flex shrink-0 gap-1">
                            <button
                                class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
                                @click.prevent="compareStore.toggleCompare(data.id)"
                            >
                                <IconScales
                                    :class="['w-4 h-4', compareStore.isCompared(data.id) ? 'text-blue-500' : 'text-gray-700']"
                                />
                            </button>

                            <button
                                class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
                                @click.prevent="likeStore.toggleLike(data.id)"
                            >
                                <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="w-4 h-4 text-likeColor"/>
                                <IconHeart v-else class="w-4 h-4 text-gray-700"/>
                            </button>
                        </div>
                    </div>

                    <Link
                        :href="route('product', data.slug)"
                        @pointerdown="trackClick"
                        class="mt-1.5 block text-[15px] leading-[17px] font-medium text-gray-900 hover:text-navHoverColor transition line-clamp-2"
                    >
                        {{ data.title }}
                    </Link>

                    <div
                        v-if="categoryLabel"
                        class="mt-1 max-w-full truncate text-[11px] leading-4 text-gray-500"
                        :title="categoryLabel"
                    >
                        {{ categoryLabel }}
                    </div>
                </div>
            </div>

            <div class="mt-auto pt-3">
                <ProductPriceConfigurator :product-id="data.id" compact/>

                <div v-if="canBuy" class="mt-3">
                    <div class="flex items-center justify-between gap-5">
                        <CartQuantityStepper
                            v-model="qtyProxy"
                            :min="cartStore.minQty(data.id)"
                            :step="cartStore.step(data.id)"
                            :disabled="!canBuy"
                            size="sm"
                            width-class="w-[112px]"
                            @inc="cartStore.incQty(data.id)"
                            @dec="cartStore.decQty(data.id)"
                        />

                        <NumberFormatComponent
                            :number="cartStore.totalPrice(data.id)"
                            prepend_text="Итого: "
                            append_text=" ₽"
                            class="text-sm font-medium whitespace-nowrap"
                        />
                    </div>
                </div>

                <div class="mt-3">
                    <template v-if="!canBuy">
                        <button
                            @click.prevent="productStore.showFastBuyPopup(data.id)"
                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-sm font-semibold text-black transition hover:bg-amber-300"
                        >
                            Узнать цену
                        </button>
                    </template>

                    <template v-else>
                        <div class="flex gap-2">
                            <template v-if="cartStore.isInCartExact(data.id)">
                                <Link
                                    :href="route('cart')"
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500"
                                >
                                    <IconCheckMark class="w-5 h-5"/>
                                    Уже в смете
                                </Link>
                            </template>

                            <template v-else>
                                <button
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black transition md:hover:bg-emerald-600 md:hover:text-white"
                                    :disabled="!canBuy"
                                    @click="cartStore.addToCartFromCard(data.id)"
                                >
                                    <IconCartPlus class="h-5 w-5"/>
                                    В смету
                                </button>
                            </template>

                            <button
                                @click.prevent="productStore.showFastBuyPopup(data.id)"
                                class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 text-sm font-semibold text-black transition md:hover:bg-baseColor/80"
                            >
                                Купить сейчас
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </article>
</template>

<style scoped>
.line-clamp-1 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 1;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
